import React from "react";
import {
  Rocket,
  Target,
  Zap,
  Users,
  BarChart3,
  MessageCircle,
  ArrowRight,
  Bot,
  Instagram,
  TrendingUp,
} from "lucide-react";
import { CAMPAIGN_PRICING_PAGE } from "../OnboardingPage/routeNames";
import { useNavigate } from "react-router";

const CampaignFirstView = () => {
  const navigate = useNavigate();
  const features = [
    {
      icon: <Bot className="w-6 h-6 text-blue-500" />,
      title: "Automated Responses",
      description:
        "Set up intelligent auto-replies to engage with your audience instantly",
    },
    {
      icon: <Target className="w-6 h-6 text-blue-500" />,
      title: "Smart Targeting",
      description:
        "Reach the right audience with precision targeting and segmentation",
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-blue-500" />,
      title: "Advanced Analytics",
      description:
        "Track performance with detailed insights and conversion metrics",
    },
    {
      icon: <MessageCircle className="w-6 h-6 text-blue-500" />,
      title: "Engagement Tools",
      description: "Boost interaction with customized engagement workflows",
    },
  ];

  const stats = [
    { value: "2x", label: "Faster Response Time" },
    { value: "60%", label: "More Engagement" },
    { value: "24/7", label: "Active Management" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div style={{ width: "100%" }} className="bg-white">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-blue-100 p-3 rounded-full">
                <Rocket className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Transform Your Instagram Engagement
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Automate your Instagram responses, grow your audience, and boost
              engagement with smart campaign management.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => navigate(CAMPAIGN_PRICING_PAGE)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700 transition-colors"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </button>
              <button className="bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors">
                View Demo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="bg-blue-500 rounded-xl p-6">
                <p className="text-4xl font-bold mb-2">{stat.value}</p>
                <p className="text-blue-100">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Everything You Need to Succeed
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Powerful features to help you manage your Instagram campaigns
            effectively
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">{feature.icon}</div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Social Proof */}
      <div className="bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 text-gray-400 mb-8">
              <Instagram className="w-6 h-6" />
              <TrendingUp className="w-6 h-6" />
              <Users className="w-6 h-6" />
              <Zap className="w-6 h-6" />
            </div>
            <p className="text-2xl text-gray-600 text-center max-w-3xl mb-8">
              "This tool transformed how we handle our Instagram engagement. The
              automation is seamless, and our response rates have never been
              better."
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              <div>
                <p className="font-medium text-gray-900">Sarah Johnson</p>
                <p className="text-gray-600">Marketing Director @ TechFlow</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="bg-blue-700 rounded-2xl p-8 md:p-12">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Transform Your Instagram Engagement?
              </h2>
              <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
                Join thousands of successful creators and businesses who have
                already elevated their Instagram game.
              </p>
              <button
                onClick={() => navigate(CAMPAIGN_PRICING_PAGE)}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                Get Started Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignFirstView;
